import { useState, Suspense } from 'react'
import { Outlet, NavLink } from 'react-router-dom'
import PageLoadingSpinner from './PageLoadingSpinner'
import { Wrench, User, LogOut, Home } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import Logo from './Logo'
import NavBadge from './nav/NavBadge'
import NotificationsDropdown from './NotificationsDropdown'
import TermsAcceptanceModal, { useTermsAccepted } from './TermsAcceptanceModal'
import { useContractorBadgeCounts } from '../hooks/useContractorBadgeCounts'

// Navigation configuration with emoji icons for desktop sidebar
const NAV_ITEMS = {
  main: [
    { to: '/home', icon: '🏠', lucideIcon: Home, label: 'Home' },
    { to: '/jobs', icon: '🔧', lucideIcon: Wrench, label: 'Jobs' },
    { to: '/profile', icon: '👤', lucideIcon: User, label: 'Profile' },
  ],
  mobile: [
    { to: '/home', icon: Home, label: 'Home' },
    { to: '/jobs', icon: Wrench, label: 'Jobs' },
    { to: '/profile', icon: User, label: 'Profile' },
  ],
}

export default function ContractorLayout() {
  const { user, signOut } = useAuth()
  const { profile } = useProfile()
  const { getBadgeCount } = useContractorBadgeCounts()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const termsStatus = useTermsAccepted(user?.id)

  const firstName = profile?.full_name?.split(' ')[0] || profile?.company_name || 'there'

  // Show modal only when DB check confirms terms not accepted (termsStatus === false)
  // Don't show while loading (termsStatus === null) to avoid flash
  const showTermsModal = termsStatus === false && !termsAccepted

  const handleTermsAccept = () => {
    setTermsAccepted(true)
  }

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  return (
    <>
      {showTermsModal && <TermsAcceptanceModal userType="contractor" onAccept={handleTermsAccept} />}
    <div className="dashboard-layout">
      {/* Desktop Sidebar - Navy */}
      <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-64 flex-col z-30"
        style={{ background: 'var(--bg-sidebar)' }}>
        {/* Logo */}
        <div className="px-6 py-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Logo size="md" variant="light" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <p className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Contractor Portal
          </p>
          <div className="space-y-1">
            {NAV_ITEMS.main.map(({ to, icon, label }) => {
              const badgeCount = getBadgeCount(to)
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `sidebar-nav-link group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-150 ${
                      isActive ? 'sidebar-nav-link-active' : ''
                    }`
                  }
                >
                  <span className="text-base w-5 text-center transition-transform duration-150 group-hover:scale-110">{icon}</span>
                  <span className="flex-1">{label}</span>
                  <NavBadge visible={badgeCount > 0} />
                </NavLink>
              )
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="px-3 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Support is email-only: the in-app ticket feature was deleted
              (0 rows, pre-launch) and replaced with a mailto. contact-form
              already reaches the same inbox. */}
          <a
            href="mailto:support@lentals.com"
            className="sidebar-nav-link group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-150"
          >
            <span className="text-base w-5 text-center">💬</span>
            Contact support
          </a>
          <NavLink
            to="/account"
            className={({ isActive }) =>
              `sidebar-nav-link group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-150 ${
                isActive ? 'sidebar-nav-link-active' : ''
              }`
            }
          >
            <span className="text-base w-5 text-center">⚙️</span>
            Settings
          </NavLink>
          <button
            onClick={signOut}
            className="sidebar-nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] w-full transition-all duration-150"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 md:ml-64 overflow-auto min-h-screen pb-20 md:pb-0" style={{ background: 'var(--bg-surface)' }}>
        {/* Mobile Header - High contrast for outdoor/building site visibility */}
        <header
          className="md:hidden px-4 py-4 sticky top-0 z-20"
          style={{
            backgroundColor: 'var(--bg-sidebar)',
            color: 'var(--text-on-dark)'
          }}
        >
          <div className="flex items-center justify-between">
            <Logo size="sm" variant="light" />
            <div className="flex items-center gap-3">
              <span
                className="text-sm hidden sm:block"
                style={{ color: 'var(--text-on-dark-muted)' }}
              >
                {profile?.company_name || profile?.full_name}
              </span>
              <NotificationsDropdown variant="dark" />
              <button
                onClick={signOut}
                className="p-3 rounded-xl transition-all"
                style={{
                  color: 'var(--text-on-dark-muted)',
                  minWidth: '48px',
                  minHeight: '48px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                  e.currentTarget.style.color = 'var(--text-on-dark)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'var(--text-on-dark-muted)'
                }}
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden md:block sticky top-0 z-20" style={{
          background: 'var(--bg-card)',
          borderBottom: '1px solid var(--border-color)',
          padding: '20px 32px',
        }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {getGreeting()}, {firstName}
              </h1>
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                {profile?.company_name ? `${profile.company_name} - Contractor Portal` : 'Welcome to your contractor portal'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationsDropdown />
              <svg viewBox="0 0 120 120" className="w-10 h-10 rounded-xl">
                <rect width="120" height="120" rx="26" fill="#1a2744"/>
                <path d="M36 30V86H84" stroke="#ffffff" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <circle cx="84" cy="86" r="12" fill="#c87941"/>
              </svg>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="p-4 md:p-8 2xl:!max-w-[1600px]" style={{ maxWidth: 1400, width: '100%', margin: '0 auto' }}>
          <Suspense fallback={<PageLoadingSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Large touch targets for on-site use */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-20 md:hidden safe-bottom"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderTop: '1px solid var(--border-color)',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="flex">
          {NAV_ITEMS.mobile.map(({ to, icon: Icon, label }) => {
            const badgeCount = getBadgeCount(to)
            return (
              <NavLink
                key={to}
                to={to}
                className="flex-1 flex flex-col items-center justify-center text-sm font-semibold transition-all"
                style={({ isActive }) => ({
                  minHeight: '68px',
                  padding: '12px 0',
                  color: isActive ? 'var(--color-accent)' : 'var(--text-secondary)',
                  backgroundColor: isActive ? 'var(--color-accent-muted)' : 'transparent',
                  position: 'relative',
                })}
              >
                <div style={{ position: 'relative' }}>
                  <Icon className="w-6 h-6 mb-1.5" />
                  {badgeCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -2,
                        right: -6,
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-accent)',
                      }}
                      aria-label="Needs attention"
                    />
                  )}
                </div>
                {label}
              </NavLink>
            )
          })}
        </div>
      </nav>
    </div>
    </>
  )
}
